import React, { forwardRef, useImperativeHandle, useState, useEffect, useCallback } from 'react';

import api from '../../services/api';
import ProcessFormView from './ProcessFormView';

import { resolveOnboardingEmployeeNo } from './copyProcessUtils';

import ApprovalProgressTimeline from './ApprovalProgressTimeline';

import ProcessOtherSection from './ProcessOtherSection';

import './ProcessDetailContent.css';



const ProcessDetailContent = forwardRef(({

  process,

  processType,

  processTypeLabel,

  bizType = 'hr_process',

  bizId,

  showProgress = true,

  showOther = true,

  commentApi = 'hr',

  onCommentsChange,

  formEditable = false

}, ref) => {

  const [editFormData, setEditFormData] = useState(process?.form_data || {});
  const [editFormDisplay, setEditFormDisplay] = useState(process?.form_display || []);

  const [metaDraft, setMetaDraft] = useState({

    effective_date: process?.effective_date || null,

    remark: process?.remark || ''

  });



  useEffect(() => {

    setEditFormData(process?.form_data || {});
    setEditFormDisplay(process?.form_display || []);

    setMetaDraft({

      effective_date: process?.effective_date || null,

      remark: process?.remark || ''

    });

  }, [process?.id, process?.form_data, process?.form_display, process?.effective_date, process?.remark]);

  const handleFormChange = useCallback((formData, formDisplay) => {
    setEditFormData(formData);
    if (Array.isArray(formDisplay) && formDisplay.length) {
      setEditFormDisplay(formDisplay);
    }
  }, []);

  const editable = formEditable || Boolean(process?.awaiting_resubmit);

  const processForView = editable

    ? { ...process, effective_date: metaDraft.effective_date, remark: metaDraft.remark }

    : process;



  const getResubmitPayload = useCallback(async () => {
    const type = processType || process?.process_type || process?.claim_type;
    const baseForm = typeof process?.form_data === 'object' && process.form_data
      ? { ...process.form_data }
      : {};
    let form_data = { ...baseForm, ...editFormData };
    if (type === 'onboarding') {
      const employeeNo = resolveOnboardingEmployeeNo(process) || baseForm.employee_no;
      if (employeeNo) {
        try {
          const res = await api.get(`/hr/onboarding/${employeeNo}`);
          if (res.data.success) {
            form_data = res.data.data;
          }
        } catch {
          /* 保留流程内 form_data */
        }
      }
    }
    const isHrProcess = ['onboarding', 'regularization', 'transfer', 'resignation'].includes(type);
    return {
      form_data,
      ...(isHrProcess ? {
        effective_date: form_data.hire_date ?? metaDraft.effective_date,
        remark: metaDraft.remark
      } : {})
    };
  }, [editFormData, metaDraft, process, processType]);



  useImperativeHandle(ref, () => ({

    getResubmitPayload

  }), [getResubmitPayload]);



  if (!process) return null;



  const type = processType || process.process_type || process.claim_type;

  const formData = process.form_data || {};

  const displayTitle = process.title || formData.subject || process.workflow?.wf_name || processTypeLabel || type;

  const id = bizId || process.id;



  return (

    <div className="process-detail">

      <div className="process-detail-inner">

        <div className="process-detail-header">

          <h2 className="process-detail-title">{displayTitle}</h2>

        </div>



        <section className="process-detail-section">

          <h3 className="process-detail-section-title">

            申请信息

            {editable && <span className="process-detail-section-hint">（可修改后重新提交）</span>}

          </h3>

          <ProcessFormView

            processType={type}

            formData={editable ? editFormData : formData}

            process={processForView}

            formDisplay={editable && editFormDisplay.length ? editFormDisplay : process.form_display}

            editable={editable}

            onFormChange={handleFormChange}

            onMetaChange={(patch) => setMetaDraft((prev) => ({ ...prev, ...patch }))}

          />

        </section>



        {showProgress && (

          <section className="process-detail-section">

            <h3 className="process-detail-section-title">审批进度</h3>

            <ApprovalProgressTimeline process={process} />

          </section>

        )}



        {showOther && (

          <section className="process-detail-section process-detail-section--other">

            <ProcessOtherSection

              bizType={bizType}

              bizId={id}

              process={process}

              comments={process.comments || []}

              onCommentsChange={onCommentsChange}

              commentApi={commentApi}

            />

          </section>

        )}

      </div>

    </div>

  );

});



ProcessDetailContent.displayName = 'ProcessDetailContent';



export default ProcessDetailContent;

